import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { resolveImageUrl } from '@/lib/image';
import { withAffiliateTag } from '@/lib/affiliate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookDetailPage() {
  const { id } = useParams();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBook();
  }, [id]);

  const fetchBook = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/books/${id}`);
      setBook(response.data);
    } catch (error) {
      console.error('Error fetching book:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-transition py-12 px-6 md:px-12">
        <div className="container mx-auto max-w-4xl text-center py-20">
          <p className="text-muted-foreground" data-testid="book-detail-loading">Loading...</p>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="page-transition py-12 px-6 md:px-12">
        <div className="container mx-auto max-w-4xl text-center py-20">
          <p className="text-muted-foreground" data-testid="book-detail-not-found">Book not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition py-12 px-6 md:px-12">
      <div className="container mx-auto max-w-6xl">
        <Link to="/books">
          <Button variant="ghost" className="mb-8" data-testid="book-detail-back-button">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Books
          </Button>
        </Link>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Image */}
          <div className="relative h-[500px] rounded-xl overflow-hidden bg-muted" data-testid="book-detail-image">
            {book.image_url ? (
              <img
                src={resolveImageUrl(book.image_url)}
                alt={book.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="h-24 w-24 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-primary mb-2" data-testid="book-detail-title">
                {book.title}
              </h1>
              {book.author && (
                <p className="text-xl text-muted-foreground" data-testid="book-detail-author">by {book.author}</p>
              )}
              {book.illustrator && (
                <p className="text-base text-muted-foreground mt-1" data-testid="book-detail-illustrator">Illustrated by {book.illustrator}</p>
              )}
              {book.is_indian_author && (
                <span className="inline-block bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full mt-3" data-testid="book-detail-indian">
                  Indian author
                </span>
              )}
            </div>

            {book.age_range && (
              <div>
                <span className="inline-block bg-accent/10 text-accent text-sm font-semibold px-4 py-2 rounded-full" data-testid="book-detail-age">
                  {book.age_range}
                </span>
              </div>
            )}

            {book.description && (
              <div>
                <h2 className="font-heading text-2xl font-normal text-foreground mb-2">About This Book</h2>
                <p className="text-base text-muted-foreground leading-relaxed" data-testid="book-detail-description">
                  {book.description}
                </p>
              </div>
            )}

            {book.why_it_matters && (
              <div className="bg-secondary/50 p-6 rounded-xl">
                <h2 className="font-heading text-2xl font-normal text-foreground mb-2">Why It Matters</h2>
                <p className="text-base text-muted-foreground leading-relaxed" data-testid="book-detail-why-matters">
                  {book.why_it_matters}
                </p>
              </div>
            )}

            {book.themes && book.themes.length > 0 && (
              <div>
                <h3 className="font-body text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">Themes</h3>
                <div className="flex flex-wrap gap-2" data-testid="book-detail-themes">
                  {book.themes.map((theme, index) => (
                    <span
                      key={index}
                      className="inline-block bg-muted text-foreground text-sm px-3 py-1 rounded-md"
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {book.buy_link && (
              <Button className="rounded-full w-full" size="lg" asChild data-testid="book-detail-buy-button">
                <a href={withAffiliateTag(book.buy_link)} target="_blank" rel="noopener noreferrer">
                  Find This Book
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}